import type { ColorType } from '@twlvxtwlv/types'
import type { ReactNode } from 'react'
import { forwardRef, useImperativeHandle, useRef } from 'react'
import {
  Box,
  Collapse,
  Divider,
  Flex,
  Heading,
  Spacer,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { neueMachina } from '../../../../fonts'
import { HiOutlineMinusCircle, HiOutlinePlusCircle } from 'react-icons/hi2'

interface ExpandableCardProps {
  backgroundColor: ColorType
  children: ReactNode
  isExpanded?: boolean
  onCardToggle?: () => void
  title?: string | undefined | null
  titleLeftElement?: ReactNode
  titleRightElement?: ReactNode
}

export interface ExpandableCardHandles {
  expandAndScrollIntoView: () => void
}

export const ExpandableCard = forwardRef<ExpandableCardHandles, ExpandableCardProps>(
  (
    {
      backgroundColor = 'white',
      children,
      isExpanded = true,
      onCardToggle,
      title,
      titleLeftElement,
      titleRightElement,
    },
    ref
  ) => {
    const { isOpen, onToggle, onOpen } = useDisclosure({ defaultIsOpen: isExpanded })

    const stackRef = useRef<HTMLDivElement>(null)

    function handleCardToggle() {
      if (onCardToggle && !isOpen) {
        onCardToggle()
      }
      onToggle()
    }

    useImperativeHandle(ref, () => ({
      expandAndScrollIntoView: () => {
        onOpen()
        stackRef?.current?.scrollIntoView()
      },
    }))

    return (
      <Stack
        bg={backgroundColor}
        color={backgroundColor === 'white' ? 'black' : 'white'}
        spacing={0}
        w="full"
        h="full"
        maxH="full"
        flexGrow={1}
        align="flex-start"
        justify="flex-start"
        boxShadow="md"
        rounded={20}
        pt={[0, 0, 0, 0, 2]}
        px={[6, 6, 6, 6, 8]}
        ref={stackRef}
      >
        {title ? (
          <Flex
            w="full"
            maxW="full"
            align="center"
            h="42px"
            minH="42px"
            maxH="42px"
            onClick={() => {
              handleCardToggle()
            }}
            _hover={{ cursor: 'pointer' }}
            gap={[2, 2, 4]}
          >
            <Flex
              w="full"
              maxW="full"
              gap={1}
              flexGrow={1}
              flexShrink={1}
              textOverflow="ellipsis"
              overflow="hidden"
              align="baseline"
            >
              {titleLeftElement ? (
                <Text
                  pr={1}
                  fontFamily={neueMachina.style.fontFamily}
                  fontWeight="600"
                  fontSize={['sm', 'sm', 'md', 'md', 'xl']}
                  textTransform="uppercase"
                  whiteSpace="nowrap"
                >
                  {titleLeftElement}
                </Text>
              ) : null}

              <Heading
                as="h2"
                fontWeight="400"
                fontSize={['sm', 'sm', 'md', 'md', 'xl']}
                textTransform="uppercase"
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
              >
                {title}
              </Heading>
            </Flex>

            <Flex mr={[-2, -2, -4]} gap={1} flexGrow={1} flexShrink={0}>
              {titleRightElement ? titleRightElement : null}
              <Spacer />
              <Box as={isOpen ? HiOutlineMinusCircle : HiOutlinePlusCircle} size={24} />
            </Flex>
          </Flex>
        ) : null}
        <Collapse style={{ width: '100%' }} in={isOpen} animateOpacity>
          <Stack>
            <Divider
              w={20}
              my={2}
              alignSelf="flex-start"
              borderColor={backgroundColor === 'white' ? 'black' : 'white'}
            />
            <Flex w="full" direction="row" gap={0} pt={2} pb={[6, 6, 8]}>
              {children}
            </Flex>
          </Stack>
        </Collapse>
      </Stack>
    )
  }
)
ExpandableCard.displayName = 'ExpandableCard'
